import { formatIsoDate, formatLimit, formatUsd } from "@/lib/displayFormat";
import type {
  BrokerPaymentPlan,
  BrokerQuote,
  CarrierIndication,
  CarrierDecline,
  DeclinedCarrier,
  SubmissionCarrier,
  CarrierStepError,
  IndicationResult,
  OfferTermView,
  OfferTermsGroupView,
} from "@/lib/submissions/brokerSubmissions";
import {
  selectable,
  toColumns,
  type OfferColumn,
} from "@/lib/submissions/comparisonColumns";
import {
  REFERRAL_NEXT,
  REFERRED_LABEL,
  appetiteLine,
  outcomeLabel,
  referralLine,
} from "@/lib/submissions/outcomes";
import {
  openBeforeBinding,
  referralStanding,
} from "@/lib/submissions/quoteConditions";
import {
  isOpenReview,
  REVIEW_LABELS,
  reviewChip,
  reviewLine,
} from "@/lib/submissions/quoteReview";
import {
  bindStanding,
  isChosen,
  isExpired,
  planOf,
  PAYMENT_PLAN_LABELS,
} from "@/lib/submissions/quoteBinding";
import { utcTodayIso } from "@/lib/dates";
import { manualQuoteLine } from "@/lib/submissions/manualQuotes";
import { coveragePartsOf } from "@/lib/submissions/coverageRows";
import {
  readCarrierProfile,
  type BrokerCarrierSummary,
} from "@/lib/brokerCarriers";

// The quote cards (BRK-S347): one card per carrier offer, in the order a
// broker compares them, each with its status, a few facts and this visit's
// quote. Everything is read from what PMS sends today (NN #3); a
// carrier's rating detail comes with PMS-S745 (BRK-S346).

/** This visit's quote for one carrier (NN #9: never stored). */
export type FirmRun =
  /** Its questions are open; `note` says why, when it came back to them. */
  (
    | {
        kind: "answering";
        carrierId: string;
        note: string | null;
        declined?: CarrierDecline;
      }
    | { kind: "pricing"; carrierId: string }
    /** A quote Submit had PMS record (PMS-S815) that didn't come back: PMS's
     *  job keeps trying, and the page reads the quotes again until a quote
     *  newer than `since` (`quoteSignature`) arrives. `inProgress` while
     *  another caller holds the request. */
    | {
        kind: "waiting";
        carrierId: string;
        since: string | null;
        inProgress: boolean;
      }
    /** Refused while the broker was elsewhere; shown when they come back. */
    | { kind: "failed"; carrierId: string; problem: CarrierStepError }
    | { kind: "done"; carrierId: string; result: IndicationResult }
  ) & { carrier?: string };

/** The visit's quotes, by card key. */
export type FirmRuns = Readonly<Record<string, FirmRun>>;

/** A chip: its words and its `statusBadgeClass` palette. */
export interface QuoteChip {
  label: string;
  tone: string;
}

export interface QuoteFact {
  label: string;
  value: string;
}

export interface QuoteCard {
  /** Stable from the fan-out, to the saved offer, to the quote. */
  key: string;
  carrier: string;
  column: OfferColumn;
  carrierProfile?: BrokerCarrierSummary | null;
  /** The price on show; null for a carrier with none. */
  quote: BrokerQuote | null;
  chip: QuoteChip;
  /** The cheapest current offer, when two or more compare. */
  lowest: boolean;
  /** At most three, never the total again. */
  facts: QuoteFact[];
  /** Why a carrier has no price, in a line. */
  summary: string | null;
  /** The fan-out's reasons and this visit's refer or decline reasons. */
  reasons: string[];
  run: FirmRun | null;
  /** Whether "Get a quote" can start or continue for this carrier. */
  canFirmUp: boolean;
  /** Its quote's validity has passed (PMS-S816, BRK-S462): it can't be
   *  selected or bound until it's priced again. */
  expired: boolean;
  /** How many of its subjectivities are open before binding, in a line
   *  (PMS-S749, BRK-S362); null with none. */
  conditions: string | null;
}

/** Whether a carrier's quote is being fetched right now: this visit's
 *  request, or one another caller holds (PMS-S815). */
export function fetchingNow(run: FirmRun | null | undefined): boolean {
  return run?.kind === "pricing" || (run?.kind === "waiting" && run.inProgress);
}

/** Whether a carrier's quote is on its way: being fetched, or one PMS keeps
 *  trying after it didn't answer. */
export function onItsWay(run: FirmRun | null | undefined): boolean {
  return run?.kind === "pricing" || run?.kind === "waiting";
}

/** Whether a card shows a price: a declined carrier's indication isn't one,
 *  and neither is an earlier quote while the new one is on its way
 *  (BRK-S460), so an outdated price never reads as the answer. */
export function showsPrice(
  card: QuoteCard,
): card is QuoteCard & { quote: BrokerQuote } {
  return (
    card.quote !== null && card.chip.label !== "Declined" && !onItsWay(card.run)
  );
}

/** The carriers whose quotes are being fetched now, in card order: while
 *  any is, the page shows its loading screen in place of the cards
 *  (BRK-S460). */
export function quotesOnTheirWay(cards: readonly QuoteCard[]): string[] {
  return cards.filter((c) => fetchingNow(c.run)).map((c) => c.carrier);
}

/** A carrier's current offer, as one comparable string: a new quote, or the
 *  same one priced again (PMS-S707), reads differently. Null with none. */
export function quoteSignature(
  quotes: readonly BrokerQuote[],
  carrierId: string,
): string | null {
  const q = quotes.find((x) => x.carrierId === carrierId);
  return q ? `${q.id}|${q.kind}|${q.repricedAt ?? q.createdAt}` : null;
}

/** A quote that came back this visit, so its card shows it before the
 *  quotes list is read again. */
function runQuotes(runs: FirmRuns): BrokerQuote[] {
  return Object.values(runs).flatMap((r) =>
    r.kind === "done" && r.result.quote ? [r.result.quote] : [],
  );
}

function slug(carrier: string): string {
  return carrier
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Keep existing name-based popup links. Colliding names use the carrier id,
 *  so an offer's link never follows another carrier when the list reorders.
 *  Only legacy columns without an address need a numeric disambiguator. */
function cardKeys(columns: OfferColumn[]): string[] {
  const names = columns.map((c) => c.carrier && slug(c.carrier));
  const used = new Set<string>();
  return columns.map((c, i) => {
    const named = names[i];
    const ambiguous =
      named && names.filter((name) => name === named).length > 1;
    const base =
      ambiguous && c.carrierId
        ? `carrier-${c.carrierId}`
        : (c.carrier && slug(c.carrier)) || `quote-${c.quote?.id ?? c.key}`;
    let key = base;
    for (let n = 2; used.has(key); n++) key = `${base}-${n}`;
    used.add(key);
    return key;
  });
}

/** The popup key the comparison gives a saved quote's card, for a link to it
 *  from elsewhere on the page (the submission's Actions, BRK-S464); null
 *  when no card holds it. */
export function quoteCardKey(
  quotes: BrokerQuote[],
  declined: DeclinedCarrier[],
  quoteId: string,
): string | null {
  return (
    toQuoteCards(quotes, null, {}, declined).find(
      (c) => c.quote?.id === quoteId,
    )?.key ?? null
  );
}

/** The policy period as m/d/yyyy; null when the quote names no dates. */
export function policyPeriod(q: BrokerQuote): string | null {
  if (!q.policyEffectiveDate) return null;
  const from = formatIsoDate(q.policyEffectiveDate);
  return q.policyExpirationDate
    ? `${from} – ${formatIsoDate(q.policyExpirationDate)}`
    : `From ${from}`;
}

/** A payment plan as a sentence, with as much as the carrier stated. */
export function planSentence(p: BrokerPaymentPlan): {
  label: string;
  text: string;
} {
  const plan = planOf(p);
  const label = plan ? PAYMENT_PLAN_LABELS[plan] : "Payment plan";
  if (plan === "annual") {
    const text = p.downPayment
      ? `One payment of ${formatUsd(p.downPayment)}`
      : "One payment";
    return { label, text };
  }
  const n = p.numberOfInstallments;
  const amount = p.installmentAmount ? formatUsd(p.installmentAmount) : null;
  const rest =
    n && amount
      ? `${n} payments of ${amount}`
      : n
        ? `${n} payments`
        : amount
          ? `payments of ${amount}`
          : "installments";
  const text = p.downPayment
    ? `${formatUsd(p.downPayment)} down, then ${rest}`
    : rest.charAt(0).toUpperCase() + rest.slice(1);
  return { label, text };
}

/** Employers liability, only the limits the carrier stated. */
export function employersLiability(q: BrokerQuote): QuoteFact[] {
  const limits: [string, string | null][] = [
    ["Each accident", q.elEachAccident],
    ["Disease, each employee", q.elEachEmployee],
    ["Disease, policy limit", q.elEachPolicy],
  ];
  return limits.flatMap(([label, value]) =>
    value === null ? [] : [{ label, value: formatLimit(value) }],
  );
}

/** A resolved requested term, formatted by its manifest type. */
export function formatTermValue(term: OfferTermView): string {
  const v = term.value;
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (term.type === "currency") return formatUsd(String(v));
  return String(v);
}

/** The insured's requested terms: the same on every carrier's quote, so any
 *  priced card carries them. */
export function requestedTerms(cards: QuoteCard[]): OfferTermsGroupView[] {
  return cards.find((c) => c.quote)?.quote?.offerTerms ?? [];
}

function chipFor(
  column: OfferColumn,
  quote: BrokerQuote | null,
  run: FirmRun | null,
  expired: boolean,
): QuoteChip {
  if (fetchingNow(run)) return { label: "Pricing…", tone: "in_progress" };
  // A carrier Signal quotes by hand, until its quote is in (BRK-S395).
  if (column.manual && !quote)
    return column.manual.request && column.manual.request.status !== "cancelled"
      ? { label: "Requested", tone: "in_progress" }
      : { label: "Quoted by Signal", tone: "draft" };
  if (run?.kind === "waiting") return { label: "Waiting", tone: "in_progress" };
  if (run?.kind === "failed")
    return { label: "Didn’t go through", tone: "error" };
  if (run?.kind === "answering" && run.declined)
    return { label: "Declined", tone: "declined" };
  if (run?.kind === "answering" && "declined" in run && !quote)
    return { label: "Questions", tone: "draft" };
  const result = run?.kind === "done" ? run.result : null;
  // Where the broker is with it, once chosen or asked to bind (PMS-S554/
  // S555, BRK-S209): "Bound" only once PMS has it bound.
  const bind = bindStanding(quote);
  if (bind) return { label: bind.chip, tone: bind.tone };
  if (quote?.outdated) return { label: "Outdated", tone: "draft" };
  if (expired) return { label: "Expired", tone: "draft" };
  // An open review, until the carrier's underwriter approves (BRK-S463), by
  // where it stands: over a declined referral too, as it was asked again.
  if (
    quote?.reviewRequest &&
    isOpenReview(quote.reviewRequest) &&
    quote.referral?.status !== "approved"
  )
    return {
      label: reviewChip(quote.reviewRequest.status),
      tone: "waiting_on_carrier",
    };
  // Signal's request to the carrier's underwriter, once it's answered
  // (PMS-S749, BRK-S362).
  const standing = referralStanding(quote?.referral);
  if (quote?.referral?.status === "declined" && standing?.chip)
    return { label: standing.chip, tone: "declined" };
  if (
    quote?.bindStatus === "decline" ||
    (result?.outcome === "declined" && !result.quote)
  )
    return { label: "Declined", tone: "declined" };
  // A carrier that prices only when it quotes refers what it priced, a quote
  // or an indication (PMS-S750, BRK-S364).
  if (
    (quote?.bindStatus === "refer" &&
      (quote.kind === "quote" || quote.pricesOnlyAtQuote)) ||
    (result?.outcome === "referred" && !result.quote)
  )
    return quote?.referral?.status === "approved" && standing?.chip
      ? { label: standing.chip, tone: "quoted" }
      : { label: REFERRED_LABEL, tone: "referred" };
  if (quote?.kind === "quote") return { label: "Quote", tone: "quoted" };
  if (quote) return { label: "Indication", tone: "draft" };
  const label = outcomeLabel(column.outcome ?? "error", column.appetite);
  switch (column.outcome) {
    case "appetite_only":
      return { label, tone: label === "Referral" ? "referred" : "draft" };
    case "referred":
    case "needs_information":
      return { label, tone: "referred" };
    case "declined":
      return { label, tone: "declined" };
    default:
      return { label, tone: "error" };
  }
}

/** How many of a quote's subjectivities are open before binding. */
function conditionsLine(quote: BrokerQuote | null): string | null {
  const open = openBeforeBinding(quote?.subjectivities);
  return open > 0 ? `${String(open)} open before binding` : null;
}

/** Where a card sorts: priced offers first, then what's still to act on,
 *  outdated and declined last. */
function rank(card: QuoteCard): number {
  const { quote, chip, column } = card;
  if (chip.label === "Declined" || quote?.referral?.status === "declined")
    return 4;
  if (quote?.outdated || card.expired) return 3;
  if (
    quote &&
    (chip.label === REFERRED_LABEL || REVIEW_LABELS.includes(chip.label))
  )
    return 1;
  if (quote) return 0;
  return column.outcome === "declined" || column.outcome === "error" ? 4 : 2;
}

function summaryFor(
  card: QuoteCard,
  problem: CarrierStepError | null,
): string | null {
  // A quote on its way says so, over whatever the card showed before
  // (BRK-S393).
  const { run } = card;
  if (fetchingNow(run)) return `Getting ${card.carrier}’s quote…`;
  if (run?.kind === "waiting")
    return `${card.carrier} didn’t answer yet. We’ll keep trying.`;
  // An open review says where it stands (BRK-S463).
  if (card.quote?.reviewRequest && REVIEW_LABELS.includes(card.chip.label))
    return reviewLine(card.quote.reviewRequest.status, card.carrier);
  // A referral says whose underwriter has to review it, priced or not
  // (BRK-S354); its reasons are the popup's.
  if (card.chip.label === REFERRED_LABEL)
    return referralLine(card.carrier, card.quote?.totalPremium ?? null);
  if (card.quote && card.chip.label !== "Declined") return null;
  if (card.column.manual)
    return manualQuoteLine(card.carrier, card.column.manual.request);
  if (problem) return problemLine(problem, card.carrier);
  if (card.reasons.length > 0) return card.reasons[0];
  const { column } = card;
  if (column.outcome === "appetite_only") return "Priced when it quotes";
  if (column.missingFields.length > 0) {
    const n = column.missingFields.length;
    return `Needs ${n} more detail${n === 1 ? "" : "s"}`;
  }
  return null;
}

/** A quote's outcome, as the carrier's step and the cards say it. A
 *  referral is an underwriter's to decide, never a bindable quote: it says
 *  whose, its estimated premium, and what to do next (BRK-S354). */
export function firmResultLine(
  carrier: string,
  result: IndicationResult,
): string {
  switch (result.outcome) {
    case "quoted":
      // A carrier that prices only when it quotes can price an indication
      // there (PMS-S750): not a bindable quote, so never "quoted".
      return result.quote?.pricesOnlyAtQuote &&
        result.quote.kind === "indication"
        ? indicationOnlyLine(carrier)
        : `${carrier} quoted.`;
    case "referred":
      return `${referralLine(carrier, result.quote?.totalPremium ?? null)} ${REFERRAL_NEXT}`;
    case "declined":
      return `${carrier} declined this application.`;
  }
}

/** An indication from a carrier that prices only when it quotes (PMS-S750,
 *  BRK-S364): not a bindable quote, and asking again only re-prices it, so
 *  its next step is the carrier's underwriter. */
export function indicationOnlyLine(carrier: string): string {
  return `${carrier} gave an indication, not a bindable quote. Its underwriter reviews it before it can be bound. ${REFERRAL_NEXT}`;
}

/** Why a quote is outdated. A carrier that prices only when it quotes says so
 *  itself too, after a change to its own questions' answers (PMS-S750), so
 *  its line names the carrier rather than the application. */
export function outdatedLine(quote: BrokerQuote, carrier: string): string {
  return quote.pricesOnlyAtQuote
    ? `Outdated — ${carrier} has to price it again, because something it was priced on has changed.`
    : "Outdated — your application changed after this quote.";
}

/** Why a quote can't be bound any more: its validity has passed (PMS-S816,
 *  BRK-S462). PMS's expired stage may come with no date to name. */
export function expiredLine(quote: BrokerQuote, carrier: string): string {
  return quote.quoteValidUntil
    ? `Expired — ${carrier}’s quote was valid until ${formatIsoDate(quote.quoteValidUntil)}.`
    : `Expired — ${carrier} has to price it again.`;
}

/** A refusal that landed while the broker was elsewhere, in a line. */
export function problemLine(p: CarrierStepError, carrier: string): string {
  switch (p.kind) {
    case "missingFields":
      return `Needs ${p.missing.length} more detail${p.missing.length === 1 ? "" : "s"}`;
    case "rejected":
      return p.messages[0] ?? `${carrier} didn’t accept these answers.`;
    case "carrier":
    case "busy":
    case "other":
      return p.message;
    case "unavailable":
      return `${carrier} isn’t responding right now.`;
    case "outdated":
      return "The application changed after it was priced.";
    case "incomplete":
      return `${carrier} has a few more questions.`;
    case "fieldErrors":
      return `${carrier} didn’t accept some answers.`;
  }
}

function factsFor(card: QuoteCard): QuoteFact[] {
  const { quote, column } = card;
  if (!quote || card.chip.label === "Declined") {
    const line = column.appetite ? appetiteLine(column.appetite) : "";
    return line ? [{ label: "Appetite", value: line }] : [];
  }
  const facts: QuoteFact[] = [];
  const period = policyPeriod(quote);
  if (period) facts.push({ label: "Policy period", value: period });
  const limits = coveragePartsOf(quote.terms).length
    ? []
    : employersLiability(quote);
  if (limits.length > 0) {
    facts.push({
      label: "Employers liability",
      value: limits.map((l) => l.value).join(" / "),
    });
  }
  // Paying in full repeats the total, so the card names an installment plan.
  const plan = quote.paymentPlans.find((p) => planOf(p) !== "annual");
  if (plan) {
    const { label, text } = planSentence(plan);
    facts.push({ label, value: text });
  }
  return facts.slice(0, 3);
}

/**
 * The cards, in the order a broker compares them:
 * 1. current prices, cheapest first;
 * 2. referred prices;
 * 3. carriers still to act on (priced when it quotes, needs information);
 * 4. outdated and expired prices;
 * 5. declined and unavailable.
 * A quote that came back this visit speaks for its carrier at once. `today`
 * is the UTC date PMS judges a quote's validity by.
 */
export function toQuoteCards(
  quotes: BrokerQuote[],
  outcomes: CarrierIndication[] | null,
  runs: FirmRuns,
  declined: DeclinedCarrier[] = [],
  today: string = utcTodayIso(),
  /** The carriers in play, for those Signal quotes by hand (BRK-S395). */
  carriers: readonly SubmissionCarrier[] = [],
): QuoteCard[] {
  const declinedIds = new Set(declined.map((d) => d.carrierId));
  const columns = toColumns(
    [...quotes, ...runQuotes(runs)],
    [
      ...(outcomes ?? []).filter((o) => !declinedIds.has(o.carrierId)),
      ...declined.map(
        (d): CarrierIndication => ({ ...d, outcome: "declined", quote: null }),
      ),
    ],
    carriers,
  );
  // A corrected decline may disappear from PMS's declined list before its
  // next quote exists. Keep the carrier's active run reachable in this visit.
  for (const run of Object.values(runs)) {
    if (run.carrier && !columns.some((c) => c.carrierId === run.carrierId))
      columns.push({
        key: run.carrierId,
        carrier: run.carrier,
        carrierId: run.carrierId,
        quote: null,
        outcome: null,
        reasons: [],
        appetite: null,
        missingFields: [],
      });
  }
  const keys = cardKeys(columns);
  const runsByCarrier = new Map(
    Object.values(runs).map((run) => [run.carrierId, run]),
  );
  const cards = columns.map((column, i): QuoteCard => {
    const key = keys[i];
    const run =
      (column.carrierId ? runsByCarrier.get(column.carrierId) : runs[key]) ??
      null;
    const result = run?.kind === "done" ? run.result : null;
    const quote = result
      ? result.quote
      : run?.kind === "answering" && run.declined
        ? null
        : column.quote;
    // An appetite-only leg's reason ("prices only when it quotes") is what its
    // card already says.
    // A saved referral keeps its reasons across a reload (PMS-S553,
    // BRK-S346); this visit's quote speaks with its own.
    const reasons = [
      ...(run?.kind === "answering" && run.declined
        ? run.declined.reasons
        : []),
      ...(column.outcome === "appetite_only" ||
      (column.outcome === "declined" &&
        (result || (run?.kind === "answering" && "declined" in run))) ||
      result?.outcome === "declined" ||
      (run?.kind === "answering" && run.declined)
        ? []
        : column.reasons),
      ...(result?.outcome === "referred" ? result.referReasons : []),
      ...(result?.outcome === "declined" ? result.declineReasons : []),
      ...(!result && quote?.bindStatus === "refer"
        ? (quote.referReasons ?? [])
        : []),
    ];
    const profile = readCarrierProfile(
      quote?.carrierProfile ?? column.carrierProfile,
    );
    const expired = quote !== null && isExpired(quote, today);
    const card: QuoteCard = {
      key,
      carrier: profile?.displayName ?? column.carrier ?? "Carrier",
      carrierProfile: profile,
      column,
      quote,
      chip: chipFor(column, quote, run, expired),
      lowest: false,
      facts: [],
      summary: null,
      reasons,
      run,
      // An expired price is asked again as an outdated one is (BRK-S462).
      canFirmUp:
        run?.kind === "answering"
          ? !run.declined
          : (selectable(column) || (expired && column.carrierId !== null)) &&
            run?.kind !== "pricing" &&
            run?.kind !== "waiting" &&
            run?.kind !== "done",
      expired,
      conditions: conditionsLine(quote),
    };
    card.facts = factsFor(card);
    card.summary = summaryFor(
      card,
      run?.kind === "failed" ? run.problem : null,
    );
    return card;
  });
  const cost = (c: QuoteCard) =>
    Number(c.quote?.terms?.totals?.totalCost ?? c.quote?.totalCost ?? Infinity);
  const ordered = cards
    .map((card, i) => ({ card, i }))
    .sort((a, b) => {
      const byRank = rank(a.card) - rank(b.card);
      if (byRank !== 0) return byRank;
      // The quote the broker chose leads its rank (BRK-S209).
      const chosen =
        Number(isChosen(b.card.quote)) - Number(isChosen(a.card.quote));
      if (chosen !== 0) return chosen;
      const priced = rank(a.card) <= 1;
      return priced && cost(a.card) !== cost(b.card)
        ? cost(a.card) - cost(b.card)
        : a.i - b.i;
    })
    .map(({ card }) => card);
  const comparable = ordered.filter((c) => rank(c) === 0);
  // The cheapest, wherever the chosen one put it.
  if (comparable.length >= 2) {
    comparable.reduce((low, c) => (cost(c) < cost(low) ? c : low)).lowest =
      true;
  }
  return ordered;
}
